// The one place live mode calls app-api (Track 2 shared setup). Two jobs:
// 1. Sign every call with the user's Cognito ID token — its `aud` is our app-client id,
//    exactly what the API's JWT gate checks (docs/frontend-auth.md).
// 2. Measure every call from day one (track2-plan.md §4): total time in the browser
//    (request → body fully downloaded) and the answer's size. Logged to the console and
//    kept in `liveCalls`, so a slow widget can be split into "the API was slow" vs
//    "drawing was slow" without new code.
//
// Fail fast (§12.2): a non-2xx answer or a missing token throws — the widget shows its
// error state; it never renders wrong or empty data.

export interface LiveCall {
  method: string;
  path: string;
  status: number;
  /** Request sent → whole body received, in ms. */
  ms: number;
  /** Body size as received (the API doesn't compress today). */
  bytes: number;
}

/** A non-2xx answer. Carries the status so an adapter can treat a KNOWN one as data —
 *  e.g. `/league-table`'s 404 "no completed matches" = an empty table — and let every
 *  other failure reach the widget's error state. */
export class LiveApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "LiveApiError";
  }
}

export interface LiveApiDeps {
  apiUrl: string;
  fetch: typeof fetch;
  getToken: () => Promise<string>;
  now: () => number;
  log: (line: string) => void;
}

export interface LiveApi {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body: unknown): Promise<T>;
  /** GET a JSON file from a signed link `/stats-file` handed out (S3, not the API: no token —
   *  the link carries its own signature). Measured like every call; logged under `label` (the
   *  link itself is long and short-lived). */
  download<T>(url: string, label: string): Promise<T>;
  /** Every call so far this session, oldest first. */
  calls: LiveCall[];
}

export function formatCall(c: LiveCall): string {
  const kb = c.bytes / 1024;
  return `[live] ${c.method} ${c.path} ${c.status} · ${Math.round(c.ms)} ms · ${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
}

export function createLiveApi(deps: LiveApiDeps): LiveApi {
  const calls: LiveCall[] = [];

  async function call<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Authorization: `Bearer ${await deps.getToken()}` };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    return measured<T>(method, path, () =>
      deps.fetch(`${deps.apiUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      })
    );
  }

  async function measured<T>(method: string, path: string, send: () => Promise<Response>): Promise<T> {
    const startedAt = deps.now();
    const res = await send();
    // Read the whole body before stopping the clock — the download is part of the cost.
    const text = await res.text();
    const entry: LiveCall = {
      method,
      path,
      status: res.status,
      ms: deps.now() - startedAt,
      bytes: new TextEncoder().encode(text).length,
    };
    calls.push(entry);
    deps.log(formatCall(entry));
    if (!res.ok) throw new LiveApiError(res.status, `${method} ${path} failed: ${res.status} ${text.slice(0, 200)}`.trim());
    return JSON.parse(text) as T;
  }

  return {
    get: (path) => call("GET", path),
    post: (path, body) => call("POST", path, body),
    // The size logged is the body as received unzipped (the browser undoes `Content-Encoding:
    // gzip` before we see it) — what the page holds, not what crossed the network.
    download: (url, label) => measured("GET", label, () => deps.fetch(url)),
    calls,
  };
}

// The page's one client, installed by the live boot (live/boot.tsx) after sign-in. Hooks
// reach it through liveApi(); calling it outside live mode is a bug, so it throws.
let installed: LiveApi | null = null;

export function installLiveApi(api: LiveApi): void {
  installed = api;
}

export function liveApi(): LiveApi {
  if (!installed) throw new Error("liveApi() called outside live mode (open the page with ?live=1).");
  return installed;
}

/** The signed-in user's ID token. Throws when there's none — in live mode every call is
 *  made after sign-in, so a missing token is a bug, not a state to paper over. */
export async function currentIdToken(): Promise<string> {
  // Imported here, not at the top: keeps Amplify out of the main bundle (it loads only
  // with ?live=1), even once the always-loaded hooks import this file.
  const { fetchAuthSession } = await import("aws-amplify/auth");
  const token = (await fetchAuthSession()).tokens?.idToken?.toString();
  if (!token) throw new Error("Live mode: not signed in (no ID token).");
  return token;
}
